import React, { useEffect, useState } from "react";
import { getOrders } from "api/orders";
import { getRoutes } from "api/routes";
import { getCommentCodes } from "api/deliveries";
import { createDeliveryGroup } from "api/deliveries";
import { Order } from "typings/models";
import { useTravelContext } from "misc/TravelContext";
import { Route, DeliveryGroup, CommentCode } from "typings/models";
// Add MUI imports
import {
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Button,
    Typography,
    Paper,
    Box,
    Collapse,
    Skeleton
} from "@mui/material";
import CustomButton from "custom/buttons/CustomButton";

export interface OrderListPageProps {
    setNextStep: () => void;
    setStep: (step: string) => void;
}

const OrderListPage: React.FC<OrderListPageProps> = ({ setNextStep, setStep }) => {
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

    const {
        setRoute,
        setAllRoutes,
        setDriver,
        setDeliveryGroup,
        setOrder,
        route,
        allRoutes,
        setCommentCodes,
        commentCodes,
        deliveryGroup,
    } = useTravelContext();

    const [showRoutes, setShowRoutes] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [noRoutes, setNoRoutes] = useState<boolean>(false);

    useEffect(() => {
        if (deliveryGroup) {
            setNextStep();
            return;
        }
        setLoading(true);
        getRoutes('driver').then((routes: Route[]) => {
            if (routes) {
                setRoute(routes[0]);
                setAllRoutes(routes);
                setSelectedOrders(routes[0].orders.map(order => order.id));
            } else {
                console.log('Error getting routes');
                setNoRoutes(true);
            }
            setLoading(false);
        });
        getCommentCodes().then((commentCodes: CommentCode[]) => {
            if (commentCodes) {
                setCommentCodes(commentCodes);
            } else {
                console.log('Error getting comment codes');
            }
        });
    }, []);

    const handleOrderSelect = (orderId: string) => {
        setSelectedOrders(prev => 
            prev.includes(orderId) 
                ? prev.filter(id => id !== orderId)
                : [...prev, orderId]
        );
    };

    const handleConfirm = () => {
        console.log('Selected orders:', selectedOrders);
        createDeliveryGroup(selectedOrders, route?.id || '').then((deliveryGroup: DeliveryGroup) => {
            if (deliveryGroup) {
                setDeliveryGroup(deliveryGroup);
                setNextStep();
                // Scroll to the top of the page after selecting a different route
                window.scrollTo(0, 0);
            } else {
                console.log('Error creating delivery group');
            }
        });
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 , mt: 5}}>
                <Typography variant="h6" gutterBottom textAlign="center">
                    Cargando Rutas...
                </Typography>
                <Skeleton variant="rectangular" height={200} />
            </Box>
        )
    }

    if (!loading && noRoutes) {
        return (
            <Box sx={{ p: 3 , mt: 5}}>
            <Typography variant="h6" gutterBottom textAlign="center">
                No hay nuevas rutas disponibles
            </Typography>
            <Typography variant="body1" gutterBottom textAlign="center">
                Contacta al administrador para crear una nueva ruta
            </Typography>
            <Typography variant="body1" gutterBottom textAlign="center">
                O puedes seleccionar una ruta existente
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button variant="contained" onClick={() => setStep("deliveredRoutes")}>
                Ver rutas existentes
            </Button>
            </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3 }}>
            {route && 
            <>
            <Typography variant="h6" gutterBottom textAlign="center">
                {route.name}
            </Typography>
            <Typography gutterBottom textAlign="center">
                {route.scheduled_date &&
                new Date(route.scheduled_date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
            </>
            }
            
            <Paper elevation={2} sx={{maxHeight: 300, overflow: 'auto'}}>
                <List>
                    {route?.orders.map((order) => (
                        <ListItem 
                            key={order.id}
                            divider
                            component="div"
                            onClick={() => handleOrderSelect(order.id)}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={selectedOrders.includes(order.id)}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography fontWeight="bold">
                                        {order.id}
                                    </Typography>
                                }
                                secondary={
                                    <>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            color="text.primary"
                                            display="block"
                                        >
                                            {order.client.name}
                                        </Typography>
                                    </>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            <Box sx={{ mt: 2 }}>
            {selectedOrders.length > 0 && <Typography variant="h6" gutterBottom textAlign="center">
                {selectedOrders.length} {selectedOrders.length === 1 ? "Orden" : "Órdenes"} {selectedOrders.length === 1 ? "Seleccionada" : "Seleccionadas"}
            </Typography>}
                <CustomButton
                    fullWidth
                    onClick={handleConfirm}
                    disabled={selectedOrders.length === 0}
                >
                    Registrar Salida
                </CustomButton>
            </Box>

        <Box sx={{ mt: 3 }}></Box>

        <Typography variant="h6" gutterBottom textAlign="center">
            ¿Ruta incorrecta?
        </Typography>
        <Typography variant="body1" gutterBottom textAlign="center">
            Selecciona una ruta diferente
        </Typography>
        
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setShowRoutes(!showRoutes)}>
                {showRoutes ? 'Ocultar rutas' : 'Mostrar rutas'}
            </Button>
        </Box>
        <Collapse in={showRoutes} timeout="auto" unmountOnExit>
        <Paper elevation={2} sx={{ mt: 3 }}>
            <List>
                {allRoutes.map((route, index) => (
                    <ListItem 
                        key={route.id}
                        sx={{ 
                            cursor: 'pointer', 
                            backgroundColor: index % 2 === 0 ? 'lightgray' : 'white' // Alternate background colors
                        }}
                        onClick={() => {
                            setRoute(route);
                            setSelectedOrders(route.orders.map(order => order.id));
                            // Scroll to the top of the page after selecting a different route
                            window.scrollTo(0, 0);
                        }}
                    >
                        <ListItemText primary={route.name + (route.scheduled_date ? 
                        " - " + new Date(route.scheduled_date).toISOString().split('T')[0] : "") } sx={{ cursor: 'pointer',
                             '&:hover': { color: 'green' } }} >
                            {route.name} 
                        </ListItemText>
                    </ListItem>
                ))}
            </List>
        </Paper>
        </Collapse>

        </Box>
    );
};

export default OrderListPage;
